import fetch from 'isomorphic-fetch';
import log from 'loglevel';
import _ from 'lodash';
import config from '../../config';
import { AcornProduct } from '../generated/types-and-hooks';
import { useQuery } from '@tanstack/react-query';

const ENV_CONFIG = config.get();
const PUBLIC_API_ROOT = ENV_CONFIG.publicApi;

export type IProductSearchAPIResponse = {
  IndexId: string;
  Version: string;
  Products: AcornProduct[] | null;
  Error?: string;
};

export const fetchPricesForTag = async (
  tag: string,
): Promise<IProductSearchAPIResponse> => {
  const result = await fetch(
    `${PUBLIC_API_ROOT}?type=tag&tag=${encodeURIComponent(tag)}`,
  );

  if (result.status >= 400) {
    throw new Error('Bad response from server');
  }
  return await result.json();
};

export const fetchProductsById = async (
  ids: string[],
): Promise<IProductSearchAPIResponse> => {
  // ids is an array of product ids
  if (_.size(ids) < 1) {
    return { IndexId: '', Version: '', Products: [] };
  }
  const result = await fetch(
    `${PUBLIC_API_ROOT}?type=id&ids=${encodeURIComponent(ids)}`,
  );

  if (result.status >= 400) {
    throw new Error('Bad response from server');
  }
  return await result.json();
};

export const searchProducts = async (
  query: string,
): Promise<IProductSearchAPIResponse> => {
  if (!query || query.length < 1) {
    log.debug(
      { query, fn: 'searchProducts' },
      'got empty query, returning empty list',
    );
    return { IndexId: 'none', Version: 'none', Products: [] };
  }
  const result = await fetch(
    `${PUBLIC_API_ROOT}?type=search&query=${encodeURIComponent(query)}`,
  );
  if (result.status >= 400) {
    throw new Error(`Bad response ${result.status} from server.`);
  }
  const resultJson = await result.json();
  return resultJson;
};

export const fetchProducts = async (
  args: any,
): Promise<IProductSearchAPIResponse> => {
  const result = await fetch(
    `${PUBLIC_API_ROOT}?${new URLSearchParams(args).toString()}`,
  );
  if (result.status >= 400) {
    throw new Error(`Bad response ${result.status} from server.`);
  }
  const resultJson = await result.json();
  log.debug({ resultJson, args }, 'got fetchProducts');
  return resultJson;
};

export enum ProductsAPIQueryType {
  InStock = 'instock',
  InStockMvg = 'instockmvg',
  Search = 'search',
  Id = 'id',
  Tag = 'tag',
}

export type ProductsAPIQuery = {
  type: ProductsAPIQueryType;
  ids?: string[]; // List of ids if ids query
  query?: string;
  tag?: string;
};

export const useProductsQuery = <
  TData = IProductSearchAPIResponse,
  TError = unknown,
>(
  variables: ProductsAPIQuery,
) =>
  useQuery<IProductSearchAPIResponse>([`search:${variables.type}`], async () =>
    fetchProducts(variables),
  );

export type HydratePricesAndStockInput = {
  Products: AcornProduct[];
};

export const useHydratePricesAndStock = <
  TData = IProductSearchAPIResponse,
  TError = unknown,
>({
  Products,
}: HydratePricesAndStockInput) => {
  const ids =
    Products && Products.length > 0 ? Products.map(({ Id }) => Id) : [];
  return useQuery<IProductSearchAPIResponse>({
    queryFn: async () => fetchProducts({ type: 'id', ids }),
    queryKey: ['hydrateProducts', ...ids],
    enabled: ids.length > 0,
    placeholderData: {
      IndexId: 'Initial',
      Version: 'Initial-Static',
      Products,
    },
  });
};
